import { useEffect } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';
import { DateTime } from 'luxon';
import { ContentNotiApi, FavoriteContentApi } from 'api/ApiHelper';
import { useAuthState } from 'contexts/AuthProvider';
import { BaseStore } from 'stores/BaseStore';

export type ContentReactionProfile = {
  favorite: boolean;
  favoriteUpdateAt: Date | null;

  noti: boolean;
  notiUpdateAt: Date | null;
};

type ContentReactionProfiles = {
  [profileId: number]: ContentReactionProfile;
};

export type ContentReaction = {
  contentId: number;

  profiles: ContentReactionProfiles;

  favoriteCount: number;
  favoriteCountUpdateAt: Date | null;

  notiCount: number;
  notiCountUpdateAt: Date | null;
};

type ContentReactions = {
  [contentId: number]: ContentReaction;
};

export interface ContentReactionState {
  contents: ContentReactions;
}

export const initialState = (): ContentReactionState => ({
  contents: {},
});

const resetContentReactionProfiles = (profileContents: ContentReactionProfiles) =>
  Object.entries(profileContents).reduce(
    (profileContentAcc, [profileId, profileContent]) => ({
      ...profileContentAcc,
      [Number(profileId)]: {
        ...profileContent,
        favorite: false,
        noti: false,
      },
    }),
    {},
  );

const resetContentReaction = (contentReactions: ContentReactions) =>
  Object.entries(contentReactions).reduce(
    (acc, [contentId, content]) => ({
      ...acc,
      [Number(contentId)]: {
        ...content,
        profiles: resetContentReactionProfiles(content.profiles),
      },
    }),
    {},
  );

export class ContentReactionStore extends BaseStore<ContentReactionState> {
  state: ContentReactionState = initialState();

  isLoggedIn = false;

  profileId?: number;

  takeSnapshot() {
    this.snapshot = { ...this.state };
  }

  load() {
    super.load();
    this.state = initialState();
  }

  setAuth(isLoggedIn: boolean, profileId?: number) {
    if (isLoggedIn === this.isLoggedIn && profileId === this.profileId) {
      return;
    }

    this.isLoggedIn = isLoggedIn;
    this.profileId = profileId;

    if (!isLoggedIn) {
      // 로그인이 풀리면 데이터를 모두 false 로 한다.
      this.state = {
        ...this.state,
        contents: resetContentReaction(this.state.contents),
      };
    } else {
      // 로그인으로 바뀌면 모든 데이터를 갱신한다.
      // FIXME 한 번에 다 갱신하면 Client 에서 느릴까?
      Promise.all(Object.entries(this.state.contents).map(contentId => this.syncMyFavorite(Number(contentId))));
      Promise.all(Object.entries(this.state.contents).map(contentId => this.syncMyNoti(Number(contentId))));
    }

    this.update();
  }

  async updateContentFavoriteCount(contentId: number, favoriteCount: number) {
    // 1분 이내라면 서버 보다 클라이언트의 정보가 맞다.
    const favoriteCountUpdateAt = this.state.contents[contentId]?.favoriteCountUpdateAt;
    if (favoriteCountUpdateAt && DateTime.fromJSDate(favoriteCountUpdateAt) > DateTime.local().plus({ minutes: -1 })) {
      return;
    }
    const isFavorited = Boolean(this.profileId && this.state.contents[contentId]?.profiles[this.profileId]?.favorite);

    this.state = {
      ...this.state,
      contents: {
        ...this.state.contents,
        [contentId]: {
          ...this.state.contents[contentId],
          profiles: this.state.contents[contentId]?.profiles ?? {},
          favoriteCount: isFavorited && favoriteCount === 0 ? 1 : favoriteCount,
          favoriteCountUpdateAt: new Date(),
        },
      },
    };

    if (await this.syncMyFavorite(contentId)) {
      this.update();
    }
  }

  async toggleFavorite(contentId: number, isFavorited: boolean) {
    if (!this.profileId) {
      return false;
    }

    this.changeFavorite(contentId, isFavorited);
    this.update();

    try {
      if (isFavorited) {
        await FavoriteContentApi.setFavoriteContent(contentId);
      } else {
        await FavoriteContentApi.releaseFavoriteContent(contentId);
      }

      return true;
    } catch (e) {
      this.changeFavorite(contentId, !isFavorited);
      this.update();

      return false;
    }
  }

  private changeFavorite(contentId: number, isFavorited: boolean) {
    if (!this.profileId) {
      return;
    }

    if (this.state.contents[contentId]?.profiles[this.profileId]?.favorite === isFavorited) {
      return;
    }

    this.state = {
      ...this.state,
      contents: {
        ...this.state.contents,
        [contentId]: {
          ...this.state.contents[contentId],
          profiles: {
            ...this.state.contents[contentId]?.profiles,
            [this.profileId]: {
              ...this.state.contents[contentId]?.profiles[this.profileId],
              favorite: isFavorited,
            },
          },
          favoriteCount: (this.state.contents[contentId]?.favoriteCount ?? 0) + (isFavorited ? 1 : -1),
          favoriteCountUpdateAt: new Date(),
        },
      },
    };
  }

  private async syncMyFavorite(contentId: number) {
    if (!this.isLoggedIn || !this.profileId) {
      return false;
    }

    try {
      const result = await FavoriteContentApi.getFavorite(contentId);
      const isFavorited = !!result?.isFavorite;
      const favoriteCount =
        isFavorited && (this.state.contents[contentId]?.favoriteCount ?? 0) === 0
          ? 1
          : this.state.contents[contentId]?.favoriteCount;

      this.state = {
        ...this.state,
        contents: {
          ...this.state.contents,
          [contentId]: {
            ...this.state.contents[contentId],
            favoriteCount,
            profiles: {
              ...this.state.contents[contentId]?.profiles,
              [this.profileId]: {
                ...this.state.contents[contentId]?.profiles[this.profileId],
                favorite: isFavorited,
                favoriteUpdateAt: new Date(),
              },
            },
          },
        },
      };

      return true;
    } catch (e) {
      return false;
    }
  }

  async updateContentNotiCount(contentId: number, notiCount: number) {
    // 1분 이내라면 서버 보다 클라이언트의 정보가 맞다.
    const notiCountUpdateAt = this.state.contents[contentId]?.notiCountUpdateAt;
    if (notiCountUpdateAt && DateTime.fromJSDate(notiCountUpdateAt) > DateTime.local().plus({ minutes: -1 })) {
      return;
    }

    const isNoti = Boolean(this.profileId && this.state.contents[contentId]?.profiles[this.profileId]?.noti);

    this.state = {
      ...this.state,
      contents: {
        ...this.state.contents,
        [contentId]: {
          ...this.state.contents[contentId],
          profiles: this.state.contents[contentId]?.profiles ?? {},
          notiCount: isNoti && notiCount === 0 ? 1 : notiCount,
          notiCountUpdateAt: new Date(),
        },
      },
    };

    if (await this.syncMyNoti(contentId)) {
      this.update();
    }
  }

  async toggleNoti(contentId: number, isFavorited: boolean) {
    if (!this.profileId) {
      return false;
    }

    this.changeNoti(contentId, isFavorited);
    this.update();

    try {
      if (isFavorited) {
        await ContentNotiApi.addNewEpisodeNoti(contentId);
      } else {
        await ContentNotiApi.deleteNewEpisodeNoti(contentId);
      }

      return true;
    } catch (e) {
      this.changeNoti(contentId, !isFavorited);
      this.update();

      return false;
    }
  }

  private changeNoti(contentId: number, isNoti: boolean) {
    if (!this.profileId) {
      return;
    }

    if (this.state.contents[contentId]?.profiles[this.profileId]?.noti === isNoti) {
      return;
    }

    this.state = {
      ...this.state,
      contents: {
        ...this.state.contents,
        [contentId]: {
          ...this.state.contents[contentId],
          profiles: {
            ...this.state.contents[contentId]?.profiles,
            [this.profileId]: {
              ...this.state.contents[contentId]?.profiles[this.profileId],
              noti: isNoti,
            },
          },

          notiCount: (this.state.contents[contentId]?.notiCount ?? 0) + (isNoti ? 1 : -1),
          notiCountUpdateAt: new Date(),
        },
      },
    };
  }

  private async syncMyNoti(contentId: number) {
    if (!this.isLoggedIn || !this.profileId) {
      return false;
    }

    try {
      const result = await ContentNotiApi.getNewEpisodeNoti(contentId);
      const isNoti = !!result?.isEnabled;
      const notiCount =
        isNoti && (this.state.contents[contentId]?.notiCount ?? 0) === 0
          ? 1
          : this.state.contents[contentId]?.notiCount;

      this.state = {
        ...this.state,
        contents: {
          ...this.state.contents,
          [contentId]: {
            ...this.state.contents[contentId],
            notiCount,
            profiles: {
              ...this.state.contents[contentId]?.profiles,
              [this.profileId]: {
                ...this.state.contents[contentId]?.profiles[this.profileId],
                noti: isNoti,
                notiUpdateAt: new Date(),
              },
            },
          },
        },
      };

      return true;
    } catch (e) {
      return false;
    }
  }
}

export const contentReactionStore = new ContentReactionStore();

export const useContentReactionStore = (): {
  snapshot: ContentReactionState;
  store: ContentReactionStore;
} => {
  const { isLoggedIn, profile } = useAuthState();

  const snapshot = useSyncExternalStoreWithSelector(
    onStoreChange => {
      contentReactionStore.addListener(onStoreChange);
      return () => contentReactionStore.removeListener(onStoreChange);
    },
    () => initialState(),
    () => contentReactionStore.getSnapshot(),
    () => contentReactionStore.getSnapshot(),
  );

  useEffect(() => {
    contentReactionStore.setAuth(isLoggedIn, profile?.id);
  }, [isLoggedIn, profile?.id]);

  return { snapshot, store: contentReactionStore };
};
